import { createContext, useContext, useId, type ReactNode } from 'react';
import {
  FormProvider,
  useFormContext,
  useFormState,
  type FieldValues,
  type Path,
} from 'react-hook-form';

import { cn } from '../lib/cn';
import { Banner } from '../ui/layout';
import { Button } from '../ui/button';
import { Input, type TInputSize } from '../ui/input';
import type { IApiForm } from './use-api-form';

interface IFieldContext {
  name: string;
  id: string;
  invalid: boolean;
}

const FieldContext = createContext<IFieldContext | null>(null);

const useFieldContext = (): IFieldContext => {
  const context = useContext(FieldContext);
  if (!context) throw new Error('A form control must be rendered inside <Field>');
  return context;
};

export interface IFieldControl extends IFieldContext {
  describedBy: string;
}

// For a control TextInput cannot be (a select, a switch, a chip editor): it still takes its
// name, label target and error state from the <Field> around it, never from a prop.
export const useFieldControl = (): IFieldControl => {
  const context = useFieldContext();
  return { ...context, describedBy: `${context.id}-message` };
};

const messageAt = (errors: unknown, path: string): string | undefined => {
  let node: unknown = errors;
  for (const key of path.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[key];
  }
  const message = (node as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : undefined;
};

const RejectionBanner = <TValues extends FieldValues>({
  rejection,
}: {
  rejection: IApiForm<TValues>['rejection'];
}) => {
  if (!rejection) return null;

  return (
    <Banner detail={rejection.detail}>
      {rejection.message}
      {rejection.orphans.length > 0 && (
        <span className="mt-2 block font-mono text-meta text-muted">
          {rejection.orphans.join(' · ')}
        </span>
      )}
    </Banner>
  );
};

interface IApiFormProps<TValues extends FieldValues> {
  api: IApiForm<TValues>;
  children: ReactNode;
  /** No top margin: a form that is the whole of its panel, not one section below others. */
  flush?: boolean;
  className?: string;
}

export const ApiForm = <TValues extends FieldValues>({
  api,
  children,
  flush,
  className,
}: IApiFormProps<TValues>) => (
  <FormProvider {...api.form}>
    <form
      noValidate
      onSubmit={api.submit}
      className={cn(!flush && 'mt-section', 'flex flex-col gap-field', className)}
    >
      <RejectionBanner rejection={api.rejection} />
      {children}
    </form>
  </FormProvider>
);

interface IFieldProps<TValues extends FieldValues> {
  name: Path<TValues>;
  label: string;
  hint?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}

export const Field = <TValues extends FieldValues>({
  name,
  label,
  hint,
  aside,
  children,
}: IFieldProps<TValues>) => {
  const id = useId();
  const { errors } = useFormState({ name });
  const message = messageAt(errors, name);

  return (
    <FieldContext.Provider value={{ name, id, invalid: message !== undefined }}>
      <div className="flex flex-col" data-field={name}>
        <div className="mb-label flex items-baseline justify-between gap-4">
          <label htmlFor={id} className="text-meta font-medium text-muted">
            {label}
          </label>
          {aside}
        </div>
        {children}
        {message !== undefined ? (
          <p id={`${id}-message`} className="mt-label text-meta text-danger-text">
            {message}
          </p>
        ) : (
          hint && (
            <p id={`${id}-message`} className="mt-label text-meta text-muted">
              {hint}
            </p>
          )
        )}
      </div>
    </FieldContext.Provider>
  );
};

type TTextInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'id' | 'name' | 'aria-invalid' | 'size'
> & { mono?: boolean; size?: TInputSize };

export const TextInput = ({ mono, size, ...rest }: TTextInputProps) => {
  const { name, id, invalid } = useFieldContext();
  const { register } = useFormContext();

  return (
    <Input
      id={id}
      mono={mono}
      size={size}
      invalid={invalid}
      aria-describedby={`${id}-message`}
      {...register(name)}
      {...rest}
    />
  );
};

interface ISubmitProps {
  children: ReactNode;
  waitingLabel: string;
  block?: boolean;
}

export const Submit = ({ children, waitingLabel, block = true }: ISubmitProps) => {
  const { isSubmitting } = useFormState();

  return (
    <Button type="submit" block={block} waiting={isSubmitting} waitingLabel={waitingLabel}>
      {children}
    </Button>
  );
};
